import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import useAppStore from '../../store/index';
import { useCalculationsSelector } from '../../store/selectors/calculations';
import {
    PE_PROFIT_NEUER_MITARBEITER_ID,
    PE_PROFIT_NEUE_KOSTENART_ID,
    calcAutoKundenVerlustProzent,
    PE_PROFIT_DEFAULT_UMSATZ_FAKTOR_NEU,
    PE_PROFIT_DEFAULT_UMSATZ_FAKTOR_BISHER,
    PE_PROFIT_DEFAULT_UMSATZ_AUFFANG_PROZENT,
    PlanungsBasisDatenTyp,
} from '../../types/peProfit';
import { getBwaJahreswert, resolveBwaBetriebskostenOptions } from './peProfitBwaKeys';
import { forceNumber, generateSimpleId } from '../../store/utils';
import { selectActivePeProfitWorkspace } from '../../store/peProfitWorkspace';
import { formatMoney } from '../../utils/currencyFormatter';

const safeDiv = (numerator: number, denominator: number): number =>
    denominator > 0 ? numerator / denominator : 0;

const roundEuro = (value: number): number => Math.round(Number(value));

const roundEuroString = (value: number): string => String(roundEuro(value));

/** HTML-Inputs liefern Strings — immer vor Rechenoperation parsen. */
const parseInputNumber = (raw: string | number): number =>
    roundEuro(parseFloat(String(raw).replace(',', '.')) || 0);

const parseDecimalNumber = (raw: string | number, decimals = 2): number => {
    const factor = 10 ** decimals;
    return Math.round((parseFloat(String(raw).replace(',', '.')) || 0) * factor) / factor;
};

const formatStepNumberString = (value: number, precision: 'euro' | 'decimal'): string =>
    precision === 'decimal' ? String(parseDecimalNumber(value)) : roundEuroString(value);

const toJahreswertFromEingabe = (eingabe: number, ansichtsModus: 'monat' | 'jahr', zeitraum: number): number =>
    roundEuro(ansichtsModus === 'jahr' ? eingabe : eingabe * zeitraum);

const toEingabeFromJahreswert = (jahreswert: number, ansichtsModus: 'monat' | 'jahr', zeitraum: number): number =>
    roundEuro(ansichtsModus === 'jahr' ? jahreswert : jahreswert / zeitraum);

const toMonatFromEingabe = (eingabe: number, ansichtsModus: 'monat' | 'jahr', zeitraum: number): number =>
    roundEuro(ansichtsModus === 'jahr' ? eingabe / zeitraum : eingabe);

const toEingabeFromMonat = (monat: number, ansichtsModus: 'monat' | 'jahr', zeitraum: number): number =>
    roundEuro(ansichtsModus === 'jahr' ? monat * zeitraum : monat);

const formatEuro = (value: number, currencyCode: string = 'EUR'): string =>
    formatMoney(roundEuro(value), currencyCode, 0);

/** Signed delta for live IST-vs-Ziel feedback while stepping. */
const formatSignedEuroDelta = (delta: number, currencyCode: string = 'EUR'): string => {
    const d = roundEuro(delta);
    if (d === 0) return `±0 ${currencyCode}`;
    const amount = formatEuro(Math.abs(d), currencyCode);
    return d > 0 ? `+${amount}` : `−${amount}`;
};

const IstDeltaAnzeige: React.FC<{ delta: number; periodLabel: string; currencyCode?: string }> = ({
    delta,
    periodLabel,
    currencyCode = 'EUR',
}) => {
    const d = roundEuro(delta);
    if (d === 0) return null;
    const isUp = d > 0;
    return (
        <p
            className={`text-sm font-semibold tracking-wide ${
                isUp ? 'text-rose-300/90' : 'text-emerald-400/90'
            }`}
            aria-live="polite"
        >
            {isUp ? 'Erhöhung' : 'Senkung'}: {formatSignedEuroDelta(d, currencyCode)} / {periodLabel}
        </p>
    );
};

/** Standard-PE-Tooltip: beige Hintergrund, schwarze Schrift — am Element (Feld/Schalter), nicht an Überschriften. */
const peProfitTipStyles = `
  [data-pe-tooltip] { position: relative; }
  @media (hover: hover) {
    [data-pe-tooltip]:hover::after {
      content: attr(data-pe-tooltip);
      position: absolute;
      bottom: calc(100% + 8px);
      left: 50%;
      transform: translateX(-50%);
      background-color: #E6C299;
      color: #000000;
      border: 1px solid #ffffff;
      padding: 8px 10px;
      border-radius: 4px;
      font-size: 0.75rem;
      font-weight: 800;
      line-height: 1.35;
      white-space: normal;
      width: max-content;
      max-width: min(16rem, 70vw);
      text-align: left;
      z-index: 9999;
      box-shadow: 0 4px 15px rgba(0,0,0,0.8);
      pointer-events: none;
      opacity: 0;
      animation: peProfitTipIn 0.2s forwards;
    }
    [data-pe-tooltip]:hover::before {
      content: '';
      position: absolute;
      bottom: 100%;
      left: 50%;
      transform: translateX(-50%);
      border-width: 6px;
      border-style: solid;
      border-color: #E6C299 transparent transparent transparent;
      z-index: 9999;
      pointer-events: none;
      opacity: 0;
      animation: peProfitTipIn 0.2s forwards;
    }
    [data-pe-tooltip][data-pe-tip-below]:hover::after {
      bottom: auto;
      top: calc(100% + 8px);
      animation: peProfitTipInBelow 0.2s forwards;
    }
    [data-pe-tooltip][data-pe-tip-below]:hover::before {
      bottom: auto;
      top: 100%;
      border-color: transparent transparent #E6C299 transparent;
      animation: peProfitTipInBelow 0.2s forwards;
    }
  }
  @keyframes peProfitTipIn {
    to { opacity: 1; transform: translateX(-50%) translateY(-4px); }
  }
  @keyframes peProfitTipInBelow {
    to { opacity: 1; transform: translateX(-50%) translateY(4px); }
  }
`;

/** Lokaler Monat/Jahr-Schalter nur für €-Eingabe (nicht für P&L-Ansicht). */
const EingabeEinheitToggle: React.FC<{
    value: 'monat' | 'jahr';
    onChange: (next: 'monat' | 'jahr') => void;
    compact?: boolean;
}> = ({ value, onChange, compact = false }) => (
    <div
        className={`grid grid-cols-2 gap-1 rounded-lg border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] ${
            compact ? 'p-0.5' : 'p-1'
        }`}
        role="group"
        aria-label="Eingabe-Einheit Euro"
    >
        <button
            type="button"
            onClick={() => onChange('monat')}
            data-pe-tooltip="Betrag als Monat tippen. Beispiel: 208 € Miete pro Monat."
            className={`rounded-md font-bold uppercase tracking-wider transition-colors ${
                compact ? 'py-1.5 text-[10px] px-2' : 'py-2 text-xs'
            } ${
                value === 'monat'
                    ? 'bg-[#E6C299] text-black'
                    : 'text-pe-muted dark:text-slate-400 hover:text-pe-text dark:hover:text-white'
            }`}
        >
            Monat
        </button>
        <button
            type="button"
            onClick={() => onChange('jahr')}
            data-pe-tooltip="Betrag als Jahr tippen. Beispiel: Seminar 2500 € — wird intern ÷ Zeitraum auf Monat umgerechnet."
            className={`rounded-md font-bold uppercase tracking-wider transition-colors ${
                compact ? 'py-1.5 text-[10px] px-2' : 'py-2 text-xs'
            } ${
                value === 'jahr'
                    ? 'bg-[#E6C299] text-black'
                    : 'text-pe-muted dark:text-slate-400 hover:text-pe-text dark:hover:text-white'
            }`}
        >
            Jahr
        </button>
    </div>
);

const EingabeUmrechnungHint: React.FC<{
    eingabe: number | null;
    modus: 'monat' | 'jahr';
    zeitraum: number;
    formatCurrency: (v: number) => string;
}> = ({ eingabe, modus, zeitraum, formatCurrency }) => {
    if (eingabe === null || !Number.isFinite(eingabe)) return null;
    const other =
        modus === 'jahr'
            ? roundEuro(eingabe / Math.max(1, zeitraum))
            : roundEuro(eingabe * Math.max(1, zeitraum));
    const otherLabel = modus === 'jahr' ? 'Monat' : 'Jahr';
    return (
        <p className="text-xs text-stone-500 dark:text-slate-500" aria-live="polite">
            ≈ {formatCurrency(other)} / {otherLabel}
            {modus === 'jahr' ? ` (÷ ${zeitraum})` : ` (× ${zeitraum})`}
        </p>
    );
};

/** Monat/Jahr direkt am Betragsfeld (Kosten / Investitionen). */
const BetragAlsLeiste: React.FC<{
    value: 'monat' | 'jahr';
    onChange: (next: 'monat' | 'jahr') => void;
}> = ({ value, onChange }) => (
    <div className="flex items-center justify-between gap-3">
        <span
            className="text-[10px] font-bold uppercase tracking-wider text-stone-500 dark:text-slate-500"
            data-pe-tooltip="Hier stellst du ein, ob der Betrag darunter als Monat oder als Jahr gemeint ist."
        >
            Betrag als
        </span>
        <EingabeEinheitToggle value={value} onChange={onChange} compact />
    </div>
);

const scaleEingabeStringOnModusWechsel = (
    raw: string,
    zielModus: 'monat' | 'jahr',
    zeitraum: number
): string => {
    if (raw === '' || raw === '-') return raw;
    const num = parseInputNumber(raw);
    if (num === 0 && raw !== '0') return raw;
    return zielModus === 'jahr' ? roundEuroString(num * zeitraum) : roundEuroString(num / zeitraum);
};

const formatEingabeFromMonat = (
    monat: number,
    eingabeModus: 'monat' | 'jahr',
    zeitraum: number
): string => roundEuroString(toEingabeFromMonat(monat, eingabeModus, zeitraum));

const SPIN_HIDE_CLASSES =
    'appearance-none [-moz-appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none';

const STEP_BUTTON_CLASSES =
    'flex shrink-0 items-center justify-center rounded-lg border border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-slate-800 text-lg font-bold leading-none text-pe-accent-text dark:text-[#E6C299] transition-colors hover:bg-pe-hover dark:hover:bg-slate-700 hover:text-pe-text dark:hover:text-white';

const clampStepNumber = (
    value: number,
    min?: number,
    max?: number,
    precision: 'euro' | 'decimal' = 'euro'
): number => {
    let next = precision === 'decimal' ? parseDecimalNumber(value) : roundEuro(value);
    if (min !== undefined) next = Math.max(min, next);
    if (max !== undefined) next = Math.min(max, next);
    return precision === 'decimal' ? parseDecimalNumber(next) : roundEuro(next);
};

/** Einrasten auf Referenzwert (z. B. IST), wenn der Schritt knapp daneben landet */
const resolveStepTarget = (
    next: number,
    step: number,
    snapTo?: number,
    precision: 'euro' | 'decimal' = 'euro'
): number => {
    const roundFn = (v: number) => (precision === 'decimal' ? parseDecimalNumber(v) : roundEuro(v));
    let target = roundFn(next);
    const tolerance = precision === 'decimal' ? Math.max(step / 2, 0.01) : Math.max(step / 2, 1);
    if (snapTo !== undefined && Math.abs(target - snapTo) <= tolerance) {
        target = snapTo;
    }
    return target;
};

const isGleicherMonatswert = (a: number, b: number): boolean => roundEuro(a) === roundEuro(b);

interface NumberStepInputProps {
    value: string | number;
    onChange: (value: string) => void;
    onChangeNumber?: (value: number) => void;
    /** Freie Texteingabe ohne sofortiges Runden/Parsen (z. B. mehrstellige €-Beträge) */
    typingMode?: boolean;
    onBlur?: () => void;
    step?: number;
    min?: number;
    max?: number;
    /** Anzeige-Wert zum Einrasten (z. B. IST in € / Monat oder Jahr) */
    snapTo?: number;
    precision?: 'euro' | 'decimal';
    placeholder?: string;
    className?: string;
    inputClassName?: string;
    size?: 'default' | 'compact';
    /** Beiger PE-Tooltip beim Hover über dem Eingabefeld */
    tooltip?: string;
}

const NumberStepInput: React.FC<NumberStepInputProps> = ({
    value,
    onChange,
    onChangeNumber,
    typingMode = false,
    onBlur,
    step = 10,
    min,
    max,
    snapTo,
    precision = 'euro',
    placeholder,
    className = '',
    inputClassName = '',
    size = 'default',
    tooltip,
}) => {
    const parseValue = (raw: string | number) =>
        precision === 'decimal' ? parseDecimalNumber(raw) : parseInputNumber(raw);
    const numericValue = parseValue(value);
    const buttonSize = size === 'compact' ? 'h-9 w-9 text-lg' : 'h-11 w-11 text-xl';

    const applyValue = (rawNext: number, direction?: 'down' | 'up') => {
        let next = rawNext;
        if (
            snapTo !== undefined &&
            direction === 'down' &&
            numericValue > snapTo &&
            next <= snapTo + step
        ) {
            next = snapTo;
        }
        const snapped = resolveStepTarget(next, step, snapTo, precision);
        const clamped = clampStepNumber(snapped, min, max, precision);
        if (onChangeNumber) {
            onChangeNumber(clamped);
        } else {
            onChange(formatStepNumberString(clamped, precision));
        }
    };

    return (
        <div
            className={`flex items-center gap-1 ${className}`}
            {...(tooltip ? { 'data-pe-tooltip': tooltip } : {})}
        >
            <button
                type="button"
                onClick={() => applyValue(numericValue - step, 'down')}
                className={`${STEP_BUTTON_CLASSES} ${buttonSize}`}
                aria-label="Wert verringern"
            >
                −
            </button>
            <input
                type="number"
                value={value}
                min={min}
                max={max}
                step={step}
                placeholder={placeholder}
                onChange={(e) => {
                    const raw = e.target.value;
                    if (typingMode) {
                        onChange(raw);
                        return;
                    }
                    if (onChangeNumber) {
                        const parsed = parseValue(raw);
                        const snapped = resolveStepTarget(parsed, step, snapTo, precision);
                        onChangeNumber(clampStepNumber(snapped, min, max, precision));
                    } else if (raw === '' || raw === '-') {
                        onChange(raw);
                    } else {
                        const parsed = parseValue(raw);
                        onChange(Number.isNaN(parsed) ? raw : formatStepNumberString(parsed, precision));
                    }
                }}
                onBlur={onBlur}
                className={`min-w-0 flex-1 rounded-lg border border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-[#1a1a1a] px-3 py-2.5 text-center text-base text-pe-text dark:text-white outline-none focus:border-[#E6C299]/50 ${SPIN_HIDE_CLASSES} ${inputClassName}`}
            />
            <button
                type="button"
                onClick={() => applyValue(numericValue + step, 'up')}
                className={`${STEP_BUTTON_CLASSES} ${buttonSize}`}
                aria-label="Wert erhöhen"
            >
                +
            </button>
        </div>
    );
};

interface PercentSliderProps {
    label: string;
    hint: string;
    value: number;
    min: number;
    max: number;
    step?: number;
    onChange: (value: number) => void;
}

const PercentSlider: React.FC<PercentSliderProps> = ({
    label,
    hint,
    value,
    min,
    max,
    step = 0.5,
    onChange,
}) => (
    <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
            <label className="text-base font-medium text-pe-text dark:text-slate-200">{label}</label>
            <div className="flex items-center gap-1">
                <NumberStepInput
                    value={value}
                    onChangeNumber={(next) => onChange(next)}
                    step={step}
                    min={min}
                    max={max}
                    snapTo={min}
                    precision="decimal"
                    size="compact"
                    className="w-36"
                />
                <span className="text-sm text-stone-500 dark:text-slate-500">%</span>
            </div>
        </div>
        <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e) => onChange(Number(e.target.value))}
            className="h-2 w-full accent-[#E6C299]"
        />
        <p className="text-sm leading-relaxed text-stone-500 dark:text-slate-500">{hint}</p>
    </div>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-pe-muted dark:text-slate-400">{children}</p>
);

const basisLabel = (typ: PlanungsBasisDatenTyp): string =>
    typ === 'start' ? 'Startwerte (Kostenstruktur)' : 'Ist-Werte (Gesamtleistung)';

const BASIS_OPTIONS: PlanungsBasisDatenTyp[] = ['start', 'aktuell'];

const PEProfitControlPanel: React.FC = () => {
    const calc = useCalculationsSelector();
    const {
        prognose,
        gemeinkostenDetailAnpassungen,
        personalAnpassungen,
        neueDienstleistungen,
        neueInvestitionen,
        peProfitDataVersion,
        setPreisAnpassung,
        setKundenVerlustProzent,
        setMaterialZielMonat,
        setMaterialNotiz,
        setUnternehmerUmsatzDeltaMonat,
        setUnternehmerUmsatzAuffangProzent,
        setAnsichtsModus,
        upsertGemeinkostenDetailAnpassung,
        upsertPersonalAnpassung,
        addPersonalAnpassung,
        updateNeueDienstleistung,
        removeNeueDienstleistung,
        removeGemeinkostenDetailAnpassung,
        removePersonalAnpassung,
        removeNeueInvestition,
        activeSzenarioId,
        planungsArchiv,
        peProfitBasisDatenTyp,
        updateActiveSzenarioBasis,
        setPeProfitBasisDatenTyp,
    } = useAppStore(
        useShallow((state) => {
            const ws = selectActivePeProfitWorkspace(state);
            return {
                prognose: ws.prognose,
                gemeinkostenDetailAnpassungen: ws.prognose?.gemeinkostenDetailAnpassungen,
                personalAnpassungen: ws.prognose?.personalAnpassungen,
                neueDienstleistungen: ws.prognose?.neueDienstleistungen,
                neueInvestitionen: ws.prognose?.neueInvestitionen,
                peProfitDataVersion: state.peProfitDataVersion ?? 0,
                setPreisAnpassung: state.setPreisAnpassung,
                setKundenVerlustProzent: state.setKundenVerlustProzent,
                setMaterialZielMonat: state.setMaterialZielMonat,
                setMaterialNotiz: state.setMaterialNotiz,
                setUnternehmerUmsatzDeltaMonat: state.setUnternehmerUmsatzDeltaMonat,
                setUnternehmerUmsatzAuffangProzent: state.setUnternehmerUmsatzAuffangProzent,
                setAnsichtsModus: state.setAnsichtsModus,
                upsertGemeinkostenDetailAnpassung: state.upsertGemeinkostenDetailAnpassung,
                upsertPersonalAnpassung: state.upsertPersonalAnpassung,
                addPersonalAnpassung: state.addPersonalAnpassung,
                updateNeueDienstleistung: state.updateNeueDienstleistung,
                removeNeueDienstleistung: state.removeNeueDienstleistung,
                removeGemeinkostenDetailAnpassung: state.removeGemeinkostenDetailAnpassung,
                removePersonalAnpassung: state.removePersonalAnpassung,
                removeNeueInvestition: state.removeNeueInvestition,
                activeSzenarioId: ws.activeSzenarioId,
                planungsArchiv: ws.planungsArchiv,
                peProfitBasisDatenTyp: ws.basisDatenTyp,
                updateActiveSzenarioBasis: state.updateActiveSzenarioBasis,
                setPeProfitBasisDatenTyp: state.setPeProfitBasisDatenTyp,
            };
        })
    );

    const activeBasisDatenTyp: PlanungsBasisDatenTyp = useMemo(() => {
        if (activeSzenarioId) {
            const entry = planungsArchiv.find((item) => item.id === activeSzenarioId);
            return entry?.basisDatenTyp ?? 'start';
        }
        return peProfitBasisDatenTyp;
    }, [activeSzenarioId, planungsArchiv, peProfitBasisDatenTyp]);

    const handleBasisChange = (basis: PlanungsBasisDatenTyp) => {
        if (activeSzenarioId) {
            updateActiveSzenarioBasis(basis);
        } else {
            setPeProfitBasisDatenTyp(basis);
        }
    };

    const [dlName, setDlName] = useState('');
    const [dlUmsatz, setDlUmsatz] = useState('');
    const [gkKey, setGkKey] = useState('');
    const [gkZielwert, setGkZielwert] = useState('');
    const [gkNotiz, setGkNotiz] = useState('');
    const [gkNeuName, setGkNeuName] = useState('');
    const [gkNeuDraftKey, setGkNeuDraftKey] = useState<string | null>(null);
    const [paMitarbeiterId, setPaMitarbeiterId] = useState('');
    const [paZielwert, setPaZielwert] = useState('');
    const [paNeuName, setPaNeuName] = useState('');
    const [invName, setInvName] = useState('');
    const [invMonatskosten, setInvMonatskosten] = useState('');
    const [paNeuDraftId, setPaNeuDraftId] = useState<string | null>(null);
    const [paNeuUmsatzFaktor, setPaNeuUmsatzFaktor] = useState(PE_PROFIT_DEFAULT_UMSATZ_FAKTOR_NEU);
    /** Nur €-Eingabe (unabhängig von P&L-Ansicht oben). */
    const [kostenEingabeModus, setKostenEingabeModus] = useState<'monat' | 'jahr'>('monat');

    const zeitraum = calc.zeitraum || 12;
    const ansichtsModus = prognose?.ansichtsModus ?? 'monat';
    const periodLabel = ansichtsModus === 'jahr' ? 'Jahr' : 'Monat';
    const eingabePeriodLabel = kostenEingabeModus === 'jahr' ? 'Jahr' : 'Monat';
    const moneyStep = kostenEingabeModus === 'jahr' ? 100 : 50;
    const reportMoneyStep = ansichtsModus === 'jahr' ? 100 : 50;
    const currencyCode = calc.currencyCode || 'EUR';
    const formatCurrency = useCallback(
        (value: number) => formatEuro(value, currencyCode),
        [currencyCode]
    );
    const kalkulierterKundenschwund = calcAutoKundenVerlustProzent(prognose?.preisAnpassungProzent ?? 0);
    const kundenVerlustManuell = prognose?.kundenVerlustProzent !== null && prognose?.kundenVerlustProzent !== undefined;
    const effektiverKundenschwund = kundenVerlustManuell
        ? Number(prognose?.kundenVerlustProzent ?? 0)
        : kalkulierterKundenschwund;

    const selectedPaEntry = useMemo(
        () =>
            paMitarbeiterId && paMitarbeiterId !== PE_PROFIT_NEUER_MITARBEITER_ID
                ? (personalAnpassungen ?? []).find((pa) => pa.mitarbeiterId === paMitarbeiterId)
                : undefined,
        [paMitarbeiterId, personalAnpassungen]
    );
    const paEntlassen = Boolean(selectedPaEntry?.entlassen);
    const paBisherigerUmsatzFaktor =
        selectedPaEntry?.bisherigerUmsatzFaktor ?? PE_PROFIT_DEFAULT_UMSATZ_FAKTOR_BISHER;
    const paUmsatzAuffangProzent =
        selectedPaEntry?.umsatzAuffangProzent ?? PE_PROFIT_DEFAULT_UMSATZ_AUFFANG_PROZENT;

    const istMaterialMonat = roundEuro(
        safeDiv(forceNumber((calc.bwaData as Record<string, unknown>)?.material), zeitraum)
    );
    const materialZielMonat = prognose?.materialZielMonat ?? null;
    const materialNotiz = prognose?.materialNotiz ?? '';
    const materialAnzeigeMonat = materialZielMonat ?? istMaterialMonat;
    const materialIstAnzeige = toEingabeFromMonat(istMaterialMonat, kostenEingabeModus, zeitraum);
    const materialHatAbweichung =
        materialZielMonat !== null && roundEuro(materialZielMonat) !== istMaterialMonat;
    const materialEingabeAnzeige = toEingabeFromMonat(materialAnzeigeMonat, kostenEingabeModus, zeitraum);
    const materialDelta = roundEuro(materialEingabeAnzeige - materialIstAnzeige);

    const istUnternehmerUmsatzMonat = roundEuro(Number(calc.erwarteterNettoUmsatz) || 0);
    const unternehmerUmsatzDeltaMonat = roundEuro(Number(prognose?.unternehmerUmsatzDeltaMonat) || 0);
    const unternehmerUmsatzAuffang =
        prognose?.unternehmerUmsatzAuffangProzent ?? PE_PROFIT_DEFAULT_UMSATZ_AUFFANG_PROZENT;
    const unternehmerUmsatzNettoEffektMonat =
        unternehmerUmsatzDeltaMonat === 0
            ? 0
            : unternehmerUmsatzDeltaMonat > 0
              ? unternehmerUmsatzDeltaMonat
              : -roundEuro(
                    Math.abs(unternehmerUmsatzDeltaMonat) *
                        (1 - Math.min(100, Math.max(0, unternehmerUmsatzAuffang)) / 100)
                );

    const formatAnpassungSummary = (
        istMonat: number,
        zielMonat: number,
        modus: 'monat' | 'jahr' = kostenEingabeModus
    ): string => {
        const ist = toEingabeFromMonat(Number(istMonat), modus, zeitraum);
        const ziel = toEingabeFromMonat(Number(zielMonat), modus, zeitraum);
        const delta = roundEuro(ziel - ist);
        const deltaPart = delta === 0 ? '' : ` (${formatSignedEuroDelta(delta, currencyCode)})`;
        return `${formatCurrency(ist)} → ${formatCurrency(ziel)}${deltaPart}`;
    };

    const dlUmsatzPlaceholder = `Umsatz in ${currencyCode} (${periodLabel})`;
    const dlUmsatzHint =
        ansichtsModus === 'jahr'
            ? 'Zusätzlicher Jahresumsatz durch diese Dienstleistung — Eingabe in Jahresnetto.'
            : 'Zusätzlicher Monatsumsatz — wird intern auf Jahreswert hochgerechnet.';
    const invBetragPlaceholder = `Betrag in ${currencyCode} (${eingabePeriodLabel})`;
    const invHint =
        kostenEingabeModus === 'jahr'
            ? 'Geplante Investitionen in Jahreskosten (z. B. Leasing p. a.) — Eingabe wird intern auf Monatswerte umgerechnet.'
            : 'Geplante Investitionen in Monatskosten (z. B. Leasing) — Umschalter „Betrag als“ am Feld.';

    const bwaOptions = useMemo(
        () => resolveBwaBetriebskostenOptions(calc.bwaData || {}, formatCurrency, kostenEingabeModus, zeitraum),
        [calc.bwaData, formatCurrency, kostenEingabeModus, zeitraum]
    );

    const mitarbeiterOptions = useMemo(() => {
        const produktiv = (calc.personalData || []).map((ma) => ({
            id: ma.id,
            name: ma.name || 'Unbenannt',
            gruppe: 'Produktiv',
        }));
        const unproduktiv = (calc.unproductivePersonalData || []).map((ma) => ({
            id: ma.id,
            name: ma.name || 'Unbenannt',
            gruppe: 'Unproduktiv',
        }));
        const azubi = (calc.azubiAssistenzData || []).map((ma) => ({
            id: ma.id,
            name: ma.name || 'Unbenannt',
            gruppe: 'Azubi/Assistenz',
        }));
        return [...produktiv, ...unproduktiv, ...azubi];
    }, [calc.personalData, calc.unproductivePersonalData, calc.azubiAssistenzData]);

    const bwaKeySet = useMemo(() => new Set(bwaOptions.map((o) => o.bwaKey)), [bwaOptions]);
    const isCustomGkKey = (key: string): boolean =>
        Boolean(key) && key !== PE_PROFIT_NEUE_KOSTENART_ID && !bwaKeySet.has(key);

    const customGkOptions = useMemo(
        () =>
            (gemeinkostenDetailAnpassungen ?? [])
                .filter((gk) => !bwaKeySet.has(gk.bwaKey))
                .map((gk) => ({ bwaKey: gk.bwaKey, label: gk.label })),
        [gemeinkostenDetailAnpassungen, bwaKeySet]
    );

    const selectedGkIstBetrag = useMemo(() => {
        if (!gkKey || gkKey === PE_PROFIT_NEUE_KOSTENART_ID) return null;
        if (!bwaKeySet.has(gkKey)) {
            return toEingabeFromMonat(0, kostenEingabeModus, zeitraum);
        }
        const jahreswert = getBwaJahreswert(gkKey, (calc.bwaData as Record<string, unknown>) || {});
        const istMonat = roundEuro(Number(jahreswert) / Number(zeitraum));
        return toEingabeFromMonat(istMonat, kostenEingabeModus, zeitraum);
    }, [gkKey, calc.bwaData, zeitraum, kostenEingabeModus, bwaKeySet]);

    const selectedMaIstBetrag = useMemo(() => {
        if (!paMitarbeiterId || paMitarbeiterId === PE_PROFIT_NEUER_MITARBEITER_ID) return null;
        const allMa = [
            ...(calc.personalData || []),
            ...(calc.unproductivePersonalData || []),
            ...(calc.azubiAssistenzData || []),
        ];
        const ma = allMa.find((item) => item.id === paMitarbeiterId);
        if (!ma) return null;
        const istMonat = roundEuro(forceNumber(ma.totalCost));
        return toEingabeFromMonat(istMonat, ansichtsModus, zeitraum);
    }, [
        paMitarbeiterId,
        calc.personalData,
        calc.unproductivePersonalData,
        calc.azubiAssistenzData,
        ansichtsModus,
        zeitraum,
    ]);

    const usedGkKeys = new Set((gemeinkostenDetailAnpassungen ?? []).map((g) => g.bwaKey));
    const availableGkOptions = bwaOptions.filter(
        (o) => !usedGkKeys.has(o.bwaKey) || o.bwaKey === gkKey
    );
    const usedPaIds = new Set(
        (personalAnpassungen ?? [])
            .filter((pa) => pa.aktiv && pa.mitarbeiterId !== PE_PROFIT_NEUER_MITARBEITER_ID)
            .map((pa) => pa.mitarbeiterId)
    );
    const availableMaOptions = mitarbeiterOptions.filter(
        (ma) => !usedPaIds.has(ma.id) || ma.id === paMitarbeiterId
    );
    const gkHatAbweichung = Boolean(gkKey && usedGkKeys.has(gkKey));
    const paHatAbweichung = Boolean(
        paMitarbeiterId &&
            paMitarbeiterId !== PE_PROFIT_NEUER_MITARBEITER_ID &&
            (personalAnpassungen ?? []).some(
                (pa) =>
                    pa.mitarbeiterId === paMitarbeiterId &&
                    pa.aktiv &&
                    (pa.entlassen || roundEuro(pa.neuerZielwert) !== roundEuro(pa.istWert))
            )
    );

    const gkIstReferenz =
        !gkKey || gkKey === PE_PROFIT_NEUE_KOSTENART_ID || isCustomGkKey(gkKey)
            ? 0
            : selectedGkIstBetrag ?? 0;
    const gkEingabeAktuell =
        gkZielwert === '' || gkZielwert === '-' ? null : parseInputNumber(gkZielwert);
    const gkDelta =
        gkKey && gkEingabeAktuell !== null ? roundEuro(gkEingabeAktuell - gkIstReferenz) : 0;

    const paIstReferenz =
        !paMitarbeiterId || paMitarbeiterId === PE_PROFIT_NEUER_MITARBEITER_ID
            ? 0
            : selectedMaIstBetrag ?? 0;
    const paEingabeAktuell =
        paZielwert === '' || paZielwert === '-' ? null : parseInputNumber(paZielwert);
    const paDelta =
        paMitarbeiterId && paEingabeAktuell !== null
            ? roundEuro(paEingabeAktuell - paIstReferenz)
            : 0;

    const resolveGkIstMonat = (bwaKey: string): number => {
        const jahreswert = getBwaJahreswert(bwaKey, (calc.bwaData as Record<string, unknown>) || {});
        return roundEuro(Number(jahreswert) / Number(zeitraum));
    };

    const resolveMaIstMonat = (mitarbeiterId: string): number => {
        const allMa = [
            ...(calc.personalData || []),
            ...(calc.unproductivePersonalData || []),
            ...(calc.azubiAssistenzData || []),
        ];
        const ma = allMa.find((item) => item.id === mitarbeiterId);
        return roundEuro(forceNumber(ma?.totalCost));
    };

    const commitGkZielwert = (eingabe: number, notizOverride?: string) => {
        if (!gkKey) return;
        const notizRaw = notizOverride !== undefined ? notizOverride : gkNotiz;
        const isNeu = gkKey === PE_PROFIT_NEUE_KOSTENART_ID;
        const isCustom = isNeu || isCustomGkKey(gkKey);

        if (isCustom) {
            const existing = (gemeinkostenDetailAnpassungen ?? []).find((g) => g.bwaKey === gkKey);
            const label = (isNeu ? gkNeuName : gkNeuName || existing?.label || '').trim() || 'Neue Kostenart';
            const bwaKey = isNeu ? gkNeuDraftKey ?? `custom_${generateSimpleId()}` : gkKey;
            if (isNeu && !gkNeuDraftKey) {
                setGkNeuDraftKey(bwaKey);
            }
            const neuerZielwert = toMonatFromEingabe(eingabe, kostenEingabeModus, zeitraum);
            upsertGemeinkostenDetailAnpassung({
                bwaKey,
                label,
                istWert: 0,
                neuerZielwert,
                notiz: notizRaw.trim() || undefined,
            });
            if (isNeu) {
                setGkKey(bwaKey);
                setGkNeuDraftKey(null);
                setGkNeuName(label);
            }
            return;
        }

        const option = bwaOptions.find((o) => o.bwaKey === gkKey);
        if (!option) return;
        const istMonat = resolveGkIstMonat(option.bwaKey);
        const istAnzeige = toEingabeFromMonat(istMonat, kostenEingabeModus, zeitraum);
        const snapped = resolveStepTarget(eingabe, moneyStep, istAnzeige);
        const neuerZielwert = toMonatFromEingabe(snapped, kostenEingabeModus, zeitraum);
        upsertGemeinkostenDetailAnpassung({
            bwaKey: option.bwaKey,
            label: option.label,
            istWert: istMonat,
            neuerZielwert,
            notiz: notizRaw.trim() || undefined,
        });
        if (isGleicherMonatswert(neuerZielwert, istMonat)) {
            setGkZielwert(roundEuroString(istAnzeige));
        }
    };

    const resetGkToIst = () => {
        if (!gkKey || selectedGkIstBetrag === null) return;
        setGkZielwert(roundEuroString(selectedGkIstBetrag));
        commitGkZielwert(selectedGkIstBetrag);
    };

    const commitPersonalEntlassenFelder = (
        updates: Partial<{
            entlassen: boolean;
            bisherigerUmsatzFaktor: number;
            umsatzAuffangProzent: number;
        }>
    ) => {
        if (!paMitarbeiterId || paMitarbeiterId === PE_PROFIT_NEUER_MITARBEITER_ID) return;
        const selected = mitarbeiterOptions.find((m) => m.id === paMitarbeiterId);
        const istMonat = resolveMaIstMonat(paMitarbeiterId);
        const entlassen = updates.entlassen ?? paEntlassen;
        const wiederhergestellterZiel = entlassen
            ? 0
            : roundEuro(
                  selectedPaEntry?.neuerZielwert && selectedPaEntry.neuerZielwert > 0
                      ? selectedPaEntry.neuerZielwert
                      : istMonat
              );
        upsertPersonalAnpassung({
            mitarbeiterId: paMitarbeiterId,
            mitarbeiterName: selected?.name || '',
            istWert: istMonat,
            neuerZielwert: wiederhergestellterZiel,
            entlassen,
            bisherigerUmsatzFaktor: updates.bisherigerUmsatzFaktor ?? paBisherigerUmsatzFaktor,
            umsatzAuffangProzent: updates.umsatzAuffangProzent ?? paUmsatzAuffangProzent,
        });
        if (entlassen) {
            setPaZielwert('0');
        }
    };

    const commitPersonalZielwert = (eingabe: number) => {
        if (!paMitarbeiterId) return;
        const isNeu = paMitarbeiterId === PE_PROFIT_NEUER_MITARBEITER_ID;

        if (isNeu) {
            const neuerZielwert = toMonatFromEingabe(eingabe, ansichtsModus, zeitraum);
            if (neuerZielwert <= 0) {
                if (paNeuDraftId) {
                    removePersonalAnpassung(paNeuDraftId);
                    setPaNeuDraftId(null);
                }
                setPaZielwert('0');
                return;
            }
            const name = paNeuName.trim() || 'Neuer Mitarbeiter';
            const draftId = paNeuDraftId;
            if (draftId) {
                useAppStore.getState().updatePersonalAnpassung(draftId, {
                    mitarbeiterName: name,
                    neuerZielwert,
                    umsatzFaktorNeu: paNeuUmsatzFaktor,
                });
            } else {
                addPersonalAnpassung({
                    mitarbeiterId: PE_PROFIT_NEUER_MITARBEITER_ID,
                    mitarbeiterName: name,
                    istWert: 0,
                    neuerZielwert,
                    aktiv: true,
                    umsatzFaktorNeu: paNeuUmsatzFaktor,
                });
                const created = useAppStore
                    .getState()
                    .peProfitPrognose?.personalAnpassungen?.slice(-1)[0];
                setPaNeuDraftId(created?.id ?? null);
            }
            return;
        }

        if (paEntlassen) return;

        const selected = mitarbeiterOptions.find((m) => m.id === paMitarbeiterId);
        const istMonat = resolveMaIstMonat(paMitarbeiterId);
        const istAnzeige = toEingabeFromMonat(istMonat, ansichtsModus, zeitraum);
        const snapped = resolveStepTarget(eingabe, reportMoneyStep, istAnzeige);
        const neuerZielwert = toMonatFromEingabe(snapped, ansichtsModus, zeitraum);
        upsertPersonalAnpassung({
            mitarbeiterId: paMitarbeiterId,
            mitarbeiterName: selected?.name || '',
            istWert: istMonat,
            neuerZielwert,
            entlassen: false,
        });
        if (isGleicherMonatswert(neuerZielwert, istMonat)) {
            setPaZielwert(roundEuroString(istAnzeige));
        }
    };

    const resetPersonalToIst = () => {
        if (!paMitarbeiterId || paMitarbeiterId === PE_PROFIT_NEUER_MITARBEITER_ID) return;
        if (selectedMaIstBetrag === null) return;
        setPaZielwert(roundEuroString(selectedMaIstBetrag));
        const selected = mitarbeiterOptions.find((m) => m.id === paMitarbeiterId);
        const istMonat = resolveMaIstMonat(paMitarbeiterId);
        upsertPersonalAnpassung({
            mitarbeiterId: paMitarbeiterId,
            mitarbeiterName: selected?.name || '',
            istWert: istMonat,
            neuerZielwert: istMonat,
            entlassen: false,
        });
    };

    useEffect(() => {
        if (!gkKey) {
            setGkZielwert('');
            setGkNotiz('');
            setGkNeuName('');
            setGkNeuDraftKey(null);
            return;
        }
        if (gkKey === PE_PROFIT_NEUE_KOSTENART_ID) {
            return;
        }
        const existing = (gemeinkostenDetailAnpassungen ?? []).find((g) => g.bwaKey === gkKey);
        const monatswert = existing
            ? existing.neuerZielwert
            : isCustomGkKey(gkKey)
              ? 0
              : resolveGkIstMonat(gkKey);
        setGkZielwert(formatEingabeFromMonat(monatswert, kostenEingabeModus, zeitraum));
        setGkNotiz(existing?.notiz ?? '');
        if (existing && !bwaKeySet.has(gkKey)) {
            setGkNeuName(existing.label);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Postenwechsel + Ansichtsumschaltung
    }, [gkKey, calc.bwaData, zeitraum, gemeinkostenDetailAnpassungen, kostenEingabeModus]);

    useEffect(() => {
        if (!paMitarbeiterId) {
            setPaZielwert('');
            setPaNeuDraftId(null);
            return;
        }
        if (paMitarbeiterId === PE_PROFIT_NEUER_MITARBEITER_ID) {
            return;
        }
        setPaNeuDraftId(null);
        const existing = (personalAnpassungen ?? []).find((pa) => pa.mitarbeiterId === paMitarbeiterId);
        const allMa = [
            ...(calc.personalData || []),
            ...(calc.unproductivePersonalData || []),
            ...(calc.azubiAssistenzData || []),
        ];
        const ma = allMa.find((item) => item.id === paMitarbeiterId);
        if (!ma) {
            setPaZielwert('');
            return;
        }
        const monatswert = existing
            ? existing.entlassen
                ? 0
                : existing.neuerZielwert
            : forceNumber(ma.totalCost);
        setPaZielwert(formatEingabeFromMonat(monatswert, ansichtsModus, zeitraum));
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Mitarbeiterwechsel + Ansichtsumschaltung
    }, [paMitarbeiterId, calc.personalData, calc.unproductivePersonalData, calc.azubiAssistenzData, personalAnpassungen, ansichtsModus, zeitraum]);

    const applyKostenEingabeModus = useCallback(
        (next: 'monat' | 'jahr') => {
            if (next === kostenEingabeModus) return;
            setGkZielwert((prev) => scaleEingabeStringOnModusWechsel(prev, next, zeitraum));
            setInvMonatskosten((prev) => scaleEingabeStringOnModusWechsel(prev, next, zeitraum));
            setKostenEingabeModus(next);
        },
        [kostenEingabeModus, zeitraum]
    );

    const prevAnsichtsModusRef = useRef(ansichtsModus);
    const eingabeSyncedOnceRef = useRef(false);
    useEffect(() => {
        if (!eingabeSyncedOnceRef.current) {
            eingabeSyncedOnceRef.current = true;
            setKostenEingabeModus(ansichtsModus);
            prevAnsichtsModusRef.current = ansichtsModus;
            return;
        }
        if (prevAnsichtsModusRef.current === ansichtsModus) return;
        // Report-Ansicht: nur Mitarbeiter-/DL-Drafts skalieren (Kosten haben eigenen Schalter am Feld)
        setPaZielwert((prev) => scaleEingabeStringOnModusWechsel(prev, ansichtsModus, zeitraum));
        setDlUmsatz((prev) => scaleEingabeStringOnModusWechsel(prev, ansichtsModus, zeitraum));
        prevAnsichtsModusRef.current = ansichtsModus;
    }, [ansichtsModus, zeitraum]);

    const handleAddDienstleistung = () => {
        const eingabe = parseInputNumber(dlUmsatz);
        if (eingabe <= 0) return;
        const jahresumsatzNetto = toJahreswertFromEingabe(eingabe, ansichtsModus, zeitraum);
        useAppStore.getState().addNeueDienstleistung({
            name: dlName.trim() || `Neue Dienstleistung ${(neueDienstleistungen ?? []).length + 1}`,
            jahresumsatzNetto: Number(jahresumsatzNetto),
            aktiv: true,
        });
        setDlName('');
        setDlUmsatz('');
    };

    const handleAddInvestition = () => {
        const monatskosten = toMonatFromEingabe(parseInputNumber(invMonatskosten), kostenEingabeModus, zeitraum);
        if (monatskosten <= 0) return;
        useAppStore.getState().addNeueInvestition({
            name: invName.trim() || `Investition ${(neueInvestitionen ?? []).length + 1}`,
            monatskosten: Number(monatskosten),
            aktiv: true,
        });
        setInvName('');
        setInvMonatskosten('');
    };

    const handlePlanNextNeuerMitarbeiter = () => {
        setPaNeuDraftId(null);
        setPaNeuName('');
        setPaZielwert('0');
        setPaNeuUmsatzFaktor(PE_PROFIT_DEFAULT_UMSATZ_FAKTOR_NEU);
    };

    const handleGkUebernehmenUndWeiter = () => {
        if (!gkKey) return;
        if (gkZielwert !== '') {
            commitGkZielwert(parseInputNumber(gkZielwert));
        }
        setGkKey('');
        setGkZielwert('');
        setGkNotiz('');
        setGkNeuName('');
        setGkNeuDraftKey(null);
    };

    const handlePersonalUebernehmenUndWeiter = () => {
        if (!paMitarbeiterId) return;
        if (paZielwert !== '') {
            commitPersonalZielwert(parseInputNumber(paZielwert));
        }
        if (paMitarbeiterId === PE_PROFIT_NEUER_MITARBEITER_ID) {
            handlePlanNextNeuerMitarbeiter();
        }
        setPaMitarbeiterId('');
        setPaZielwert('');
        setPaNeuName('');
        setPaNeuDraftId(null);
    };

    return (
        <aside className="pe-profit-no-print min-w-0 overflow-visible rounded-2xl border border-pe-border dark:border-[#2a2a2a] bg-pe-card dark:bg-[#0f0f0f] text-base">
            <style>{peProfitTipStyles}</style>
            <h3 className="carbon-weave-bg border-b border-pe-border dark:border-[#2a2a2a] px-5 py-4 text-sm font-semibold uppercase tracking-[0.2em] text-pe-accent-text dark:text-[#E6C299]">
                Prognose-Cockpit
            </h3>
            <div className="p-5">
            <div className="mb-6">
                <SectionTitle>Datenbasis</SectionTitle>
                <p className="mb-2 text-sm text-stone-500 dark:text-slate-500">
                    Umsatz- und Ergebnisbezug für Cockpit und Report — Startwerte aus der Kostenstruktur
                    oder aktuelle Ist-Werte aus der Gesamtleistung.
                </p>
                <div className="flex flex-col gap-2">
                    {BASIS_OPTIONS.map((option) => (
                        <button
                            key={option}
                            type="button"
                            onClick={() => handleBasisChange(option)}
                            data-pe-tooltip={
                                option === 'start'
                                    ? 'Startwerte aus der Kostenstruktur / Planung als Bezug für Umsatz und Ergebnis.'
                                    : 'Aktuelle Ist-Werte aus der Gesamtleistung als Bezug für die Prognose.'
                            }
                            data-pe-tip-below=""
                            className={`rounded-lg border px-3 py-2.5 text-left text-sm font-semibold transition-colors ${
                                activeBasisDatenTyp === option
                                    ? 'border-[#E6C299] bg-[#E6C299]/15 text-pe-accent-text dark:text-[#E6C299]'
                                    : 'border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-[#141414] text-pe-muted dark:text-slate-400 hover:border-pe-border-strong dark:hover:border-[#444] hover:text-pe-text dark:hover:text-white'
                            }`}
                        >
                            {basisLabel(option)}
                        </button>
                    ))}
                </div>
            </div>

            <div className="mb-6">
                <SectionTitle>Ansicht</SectionTitle>
                <p className="mb-2 text-sm text-stone-500 dark:text-slate-500">
                    Monat oder Jahr für den gesamten P&amp;L-Report.
                </p>
                <div className="grid grid-cols-2 gap-2 rounded-xl border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] p-1">
                    <button
                        type="button"
                        onClick={() => setAnsichtsModus('monat')}
                        data-pe-tooltip="Report zeigt alle Werte pro Monat. Zum Tippen von Jahresrechnungen nutze „Betrag als Jahr“ am Kostenfeld unten."
                        data-pe-tip-below=""
                        className={`rounded-lg py-2.5 text-sm font-bold uppercase tracking-wider transition-colors ${
                            ansichtsModus === 'monat'
                                ? 'bg-[#E6C299] text-black'
                                : 'text-pe-muted dark:text-slate-400 hover:text-pe-text dark:hover:text-white'
                        }`}
                    >
                        Monat
                    </button>
                    <button
                        type="button"
                        onClick={() => setAnsichtsModus('jahr')}
                        data-pe-tooltip="Report zeigt alle Werte als Jahreswerte (× Zeitraum). Ändert nicht, wie du Kosten tippst — das steuert „Betrag als“ am Feld."
                        data-pe-tip-below=""
                        className={`rounded-lg py-2.5 text-sm font-bold uppercase tracking-wider transition-colors ${
                            ansichtsModus === 'jahr'
                                ? 'bg-[#E6C299] text-black'
                                : 'text-pe-muted dark:text-slate-400 hover:text-pe-text dark:hover:text-white'
                        }`}
                    >
                        Jahr
                    </button>
                </div>
            </div>

            <div className="space-y-5 border-b border-pe-border dark:border-[#222] pb-6">
                <PercentSlider
                    label="Preisanpassung"
                    hint="Um wie viel Prozent planst du deine Verkaufspreise gegenüber dem Ist-Umsatz zu erhöhen?"
                    value={prognose?.preisAnpassungProzent ?? 0}
                    min={0}
                    max={50}
                    onChange={setPreisAnpassung}
                />
                <p className="rounded-lg border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] px-4 py-3 text-sm text-pe-muted dark:text-slate-400">
                    {kundenVerlustManuell ? 'Manueller' : 'Kalkulierter'} Kundenschwund:{' '}
                    <span className="font-semibold text-pe-accent-text dark:text-[#E6C299]">
                        {effektiverKundenschwund.toFixed(2)} %
                    </span>
                    {!kundenVerlustManuell && (
                        <span className="mt-1 block text-stone-500 dark:text-slate-500">
                            Automatisch nach Beratungspraxis (20 % Preiserhöhung ≈ 5 % Schwund).
                        </span>
                    )}
                </p>
                <label className="flex cursor-pointer items-center gap-2 text-sm text-pe-muted dark:text-slate-400">
                    <input
                        type="checkbox"
                        checked={kundenVerlustManuell}
                        onChange={(e) => {
                            if (e.target.checked) {
                                setKundenVerlustProzent(kalkulierterKundenschwund);
                            } else {
                                setKundenVerlustProzent(null);
                            }
                        }}
                        className="h-4 w-4 rounded border-pe-border-strong dark:border-[#444] bg-pe-elevated dark:bg-[#1a1a1a] text-pe-accent-text dark:text-[#E6C299]"
                    />
                    Kundenschwund manuell anpassen
                </label>
                {kundenVerlustManuell && (
                    <PercentSlider
                        label="Kundenschwund"
                        hint="Wie viel Prozent des Brutto-Mehrumsatzes durch Kundenverlust entfällt?"
                        value={effektiverKundenschwund}
                        min={0}
                        max={50}
                        step={0.25}
                        onChange={(value) => setKundenVerlustProzent(value)}
                    />
                )}
                <div className="space-y-2">
                    <label className="text-base font-medium text-pe-text dark:text-slate-200">Materialkosten</label>
                    <p className="text-sm leading-relaxed text-stone-500 dark:text-slate-500">
                        Geplanter Wareneinsatz in {currencyCode} — frei erhöhen oder senken gegenüber dem IST-Wert aus
                        der BWA.
                    </p>
                    <p className="text-sm text-pe-accent-text dark:text-[#E6C299]/90">
                        Aktueller IST-Wert: {formatCurrency(materialIstAnzeige)} / {eingabePeriodLabel}
                    </p>
                    <BetragAlsLeiste value={kostenEingabeModus} onChange={applyKostenEingabeModus} />
                    <NumberStepInput
                        value={toEingabeFromMonat(materialAnzeigeMonat, kostenEingabeModus, zeitraum)}
                        onChangeNumber={(next) => {
                            const snapped = resolveStepTarget(next, moneyStep, materialIstAnzeige);
                            const zielMonat = toMonatFromEingabe(snapped, kostenEingabeModus, zeitraum);
                            if (isGleicherMonatswert(zielMonat, istMaterialMonat)) {
                                setMaterialZielMonat(null);
                            } else {
                                setMaterialZielMonat(zielMonat);
                            }
                        }}
                        step={moneyStep}
                        min={0}
                        snapTo={materialIstAnzeige}
                        placeholder={`Zielwert Wareneinsatz (${currencyCode} / ${eingabePeriodLabel})`}
                        tooltip="Wareneinsatz-Ziel tippen. Zuerst „Betrag als Monat/Jahr“ wählen — Jahreswert wird automatisch umgerechnet."
                    />
                    <EingabeUmrechnungHint
                        eingabe={materialEingabeAnzeige}
                        modus={kostenEingabeModus}
                        zeitraum={zeitraum}
                        formatCurrency={formatCurrency}
                    />
                    <IstDeltaAnzeige delta={materialDelta} periodLabel={eingabePeriodLabel} currencyCode={currencyCode} />
                    {materialHatAbweichung && (
                        <input
                            type="text"
                            value={materialNotiz}
                            onChange={(e) => setMaterialNotiz(e.target.value)}
                            placeholder="Begründung / Notiz (z. B. Produktanbieterwechsel)"
                            className="w-full rounded-lg border border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-[#1a1a1a] px-3 py-2.5 text-base text-pe-text dark:text-white outline-none focus:border-[#E6C299]/50"
                        />
                    )}
                    {materialHatAbweichung && (
                        <button
                            type="button"
                            onClick={() => setMaterialZielMonat(null)}
                            className="text-sm font-medium text-pe-accent-text dark:text-[#E6C299] underline-offset-2 hover:underline"
                        >
                            Zurück auf IST-Wert ({formatCurrency(materialIstAnzeige)} / {eingabePeriodLabel})
                        </button>
                    )}
                </div>
            </div>

            <div className="mt-6 space-y-5 border-b border-pe-border dark:border-[#222] pb-6">
                <SectionTitle>Unternehmer-Umsatz</SectionTitle>
                <p className="text-sm leading-relaxed text-stone-500 dark:text-slate-500">
                    Mehr oder weniger Eigenumsatz gegenüber dem IST fließt immer in den
                    Prognose-Gesamtumsatz ein (bei Minus optional Team-Auffang).
                </p>

                <div className="space-y-2">
                    <label className="text-base font-medium text-pe-text dark:text-slate-200">
                        Unternehmer-Umsatz (Delta)
                    </label>
                    <p className="text-sm leading-relaxed text-stone-500 dark:text-slate-500">
                        IST-Eigenumsatz:{' '}
                        {formatCurrency(toEingabeFromMonat(istUnternehmerUmsatzMonat, ansichtsModus, zeitraum))}{' '}
                        netto / {periodLabel}. Negativ = weniger eigener Umsatz.
                    </p>
                    <NumberStepInput
                        value={toEingabeFromMonat(unternehmerUmsatzDeltaMonat, ansichtsModus, zeitraum)}
                        onChangeNumber={(next) => {
                            const snapped = resolveStepTarget(next, reportMoneyStep, 0);
                            setUnternehmerUmsatzDeltaMonat(
                                toMonatFromEingabe(snapped, ansichtsModus, zeitraum)
                            );
                        }}
                        step={reportMoneyStep}
                        snapTo={0}
                        placeholder={`Delta (± ${currencyCode} / ${periodLabel})`}
                        tooltip="Mehr oder weniger eigener Umsatz gegenüber IST. Negativ = weniger Eigenumsatz."
                    />
                    {unternehmerUmsatzDeltaMonat !== 0 && (
                        <IstDeltaAnzeige
                            delta={toEingabeFromMonat(unternehmerUmsatzDeltaMonat, ansichtsModus, zeitraum)}
                            periodLabel={periodLabel}
                            currencyCode={currencyCode}
                        />
                    )}
                </div>

                {unternehmerUmsatzDeltaMonat < 0 && (
                    <PercentSlider
                        label="Team fängt Umsatzrückgang auf"
                        hint="Wie viel Prozent des weniger gemachten Unternehmer-Umsatzes fängt das Team auf?"
                        value={unternehmerUmsatzAuffang}
                        min={0}
                        max={100}
                        step={5}
                        onChange={setUnternehmerUmsatzAuffangProzent}
                    />
                )}

                {unternehmerUmsatzDeltaMonat !== 0 && (
                    <p className="rounded-lg border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] px-4 py-3 text-sm text-pe-muted dark:text-slate-400">
                        Netto-Effekt auf Gesamtumsatz:{' '}
                        <span className="font-semibold text-pe-accent-text dark:text-[#E6C299]">
                            {formatSignedEuroDelta(
                                toEingabeFromMonat(unternehmerUmsatzNettoEffektMonat, ansichtsModus, zeitraum),
                                currencyCode
                            )}{' '}
                            / {periodLabel}
                        </span>
                    </p>
                )}
            </div>

            <div className="mt-6 border-b border-pe-border dark:border-[#222] pb-6">
                <SectionTitle>Gemeinkosten Detail-Anpassung</SectionTitle>
                <p className="mb-3 text-sm leading-relaxed text-stone-500 dark:text-slate-500">
                    Wähle einen BWA-Posten oder plane eine neue Kostenart ein — Änderungen wirken sofort im
                    Report. Mit <span className="text-pe-accent-text dark:text-[#E6C299]">+</span> übernimmst du den Posten und wählst
                    die nächste Kostenart. Zurück auf IST = keine Anpassung.
                </p>

                <div className="mb-3 space-y-2">
                    <select
                        value={gkKey}
                        onChange={(e) => {
                            const id = e.target.value;
                            if (id === PE_PROFIT_NEUE_KOSTENART_ID) {
                                setGkNeuDraftKey(null);
                                setGkNeuName('');
                                setGkZielwert('');
                                setGkNotiz('');
                            }
                            setGkKey(id);
                        }}
                        data-pe-tooltip="Kostenart wählen (BWA) oder „Neue Kostenart“. Danach Betrag tippen."
                        className="w-full rounded-lg border border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-[#1a1a1a] px-3 py-2.5 text-base text-pe-text dark:text-white outline-none focus:border-[#E6C299]/50"
                    >
                        <option value="">BWA-Posten wählen …</option>
                        <option value={PE_PROFIT_NEUE_KOSTENART_ID}>+ Neue Kostenart</option>
                        {availableGkOptions.map((opt) => (
                            <option key={opt.bwaKey} value={opt.bwaKey}>
                                {opt.displayLabel}
                                {usedGkKeys.has(opt.bwaKey) ? ' (bearbeiten)' : ''}
                            </option>
                        ))}
                        {customGkOptions.map((opt) => (
                            <option key={opt.bwaKey} value={opt.bwaKey}>
                                {opt.label} (neu)
                            </option>
                        ))}
                    </select>
                    {selectedGkIstBetrag !== null && gkKey !== PE_PROFIT_NEUE_KOSTENART_ID && (
                        <p className="text-sm text-pe-accent-text dark:text-[#E6C299]/90">
                            Aktueller IST-Wert:{' '}
                            {isCustomGkKey(gkKey)
                                ? `${formatCurrency(0)} (neu eingeplant)`
                                : formatCurrency(selectedGkIstBetrag)}{' '}
                            / {eingabePeriodLabel}
                        </p>
                    )}
                    {(gkKey === PE_PROFIT_NEUE_KOSTENART_ID || isCustomGkKey(gkKey)) && (
                        <input
                            type="text"
                            value={gkNeuName}
                            onChange={(e) => {
                                const name = e.target.value;
                                setGkNeuName(name);
                                if (isCustomGkKey(gkKey) && gkZielwert !== '') {
                                    upsertGemeinkostenDetailAnpassung({
                                        bwaKey: gkKey,
                                        label: name.trim() || 'Neue Kostenart',
                                        istWert: 0,
                                        neuerZielwert: toMonatFromEingabe(
                                            parseInputNumber(gkZielwert),
                                            kostenEingabeModus,
                                            zeitraum
                                        ),
                                        notiz: gkNotiz.trim() || undefined,
                                    });
                                }
                            }}
                            placeholder="Bezeichnung der Kostenart (z. B. Neue Software-Lizenz)"
                            className="w-full rounded-lg border border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-[#1a1a1a] px-3 py-2.5 text-base text-pe-text dark:text-white outline-none focus:border-[#E6C299]/50"
                        />
                    )}
                    <BetragAlsLeiste value={kostenEingabeModus} onChange={applyKostenEingabeModus} />
                    <div className="flex min-w-0 gap-2">
                        <NumberStepInput
                            value={gkZielwert}
                            typingMode={
                                gkKey === PE_PROFIT_NEUE_KOSTENART_ID || isCustomGkKey(gkKey)
                            }
                            onChange={setGkZielwert}
                            onChangeNumber={(next) => {
                                const isNeuOderCustom =
                                    gkKey === PE_PROFIT_NEUE_KOSTENART_ID || isCustomGkKey(gkKey);
                                const snapped = isNeuOderCustom
                                    ? next
                                    : resolveStepTarget(next, moneyStep, selectedGkIstBetrag ?? undefined);
                                setGkZielwert(roundEuroString(snapped));
                                if (gkKey) commitGkZielwert(snapped);
                            }}
                            onBlur={() => {
                                if (
                                    (gkKey === PE_PROFIT_NEUE_KOSTENART_ID || isCustomGkKey(gkKey)) &&
                                    gkZielwert !== '' &&
                                    gkZielwert !== '-'
                                ) {
                                    commitGkZielwert(parseInputNumber(gkZielwert));
                                }
                            }}
                            step={moneyStep}
                            min={0}
                            snapTo={
                                gkKey === PE_PROFIT_NEUE_KOSTENART_ID || isCustomGkKey(gkKey)
                                    ? undefined
                                    : selectedGkIstBetrag ?? undefined
                            }
                            placeholder={
                                gkKey === PE_PROFIT_NEUE_KOSTENART_ID || isCustomGkKey(gkKey)
                                    ? `Geplante Kosten (${currencyCode} / ${eingabePeriodLabel})`
                                    : `Zielwert (${currencyCode} / ${eingabePeriodLabel})`
                            }
                            className={`min-w-0 flex-1 ${gkKey ? '' : 'pointer-events-none opacity-40'}`}
                            tooltip="Kosten-Ziel tippen. Bei Jahresrechnung zuerst „Jahr“ wählen, dann z. B. 2500 — speichert als Monat (÷ Zeitraum)."
                        />
                        <button
                            type="button"
                            onClick={handleGkUebernehmenUndWeiter}
                            disabled={!gkKey}
                            data-pe-tooltip="Übernehmen und nächste Kostenart wählen"
                            title="Übernehmen und nächste Kostenart wählen"
                            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-[#E6C299]/40 bg-[#E6C299]/15 px-3 py-2.5 text-sm font-bold uppercase tracking-wider text-pe-accent-text dark:text-[#E6C299] hover:bg-[#E6C299]/25 disabled:opacity-40"
                        >
                            <Plus size={14} />
                        </button>
                    </div>
                    {gkKey && gkEingabeAktuell !== null && (
                        <>
                            <EingabeUmrechnungHint
                                eingabe={gkEingabeAktuell}
                                modus={kostenEingabeModus}
                                zeitraum={zeitraum}
                                formatCurrency={formatCurrency}
                            />
                            <IstDeltaAnzeige delta={gkDelta} periodLabel={eingabePeriodLabel} currencyCode={currencyCode} />
                        </>
                    )}
                    {gkHatAbweichung &&
                        selectedGkIstBetrag !== null &&
                        !isCustomGkKey(gkKey) &&
                        gkKey !== PE_PROFIT_NEUE_KOSTENART_ID && (
                        <button
                            type="button"
                            onClick={resetGkToIst}
                            className="text-sm font-medium text-pe-accent-text dark:text-[#E6C299] underline-offset-2 hover:underline"
                        >
                            Zurück auf IST-Wert ({formatCurrency(selectedGkIstBetrag)} / {eingabePeriodLabel})
                        </button>
                    )}
                    {gkKey && (
                        <input
                            type="text"
                            value={gkNotiz}
                            onChange={(e) => {
                                const notiz = e.target.value;
                                setGkNotiz(notiz);
                                if (gkZielwert !== '') {
                                    commitGkZielwert(parseInputNumber(gkZielwert), notiz);
                                }
                            }}
                            placeholder="Notiz / Zweck (z. B. Neue Werbekampagne)"
                            className="w-full rounded-lg border border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-[#1a1a1a] px-3 py-2.5 text-base text-pe-text dark:text-white outline-none focus:border-[#E6C299]/50"
                        />
                    )}
                </div>

                {(gemeinkostenDetailAnpassungen ?? []).length === 0 ? (
                    <p className="text-sm text-slate-600">Keine Detail-Anpassungen hinterlegt.</p>
                ) : (
                    <ul key={`gk-list-${peProfitDataVersion}`} className="space-y-2">
                        {(gemeinkostenDetailAnpassungen ?? []).map((gk) => (
                            <li
                                key={gk.id}
                                className="flex cursor-pointer items-center gap-2 rounded-lg border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] px-4 py-3 hover:border-[#E6C299]/30"
                                onClick={() => setGkKey(gk.bwaKey)}
                            >
                                <p className="min-w-0 flex-1 text-base text-pe-text dark:text-slate-200">
                                    <span className="text-pe-text dark:text-white">{gk.label}:</span>{' '}
                                    <span className="text-pe-accent-text dark:text-[#E6C299]">
                                        {formatAnpassungSummary(gk.istWert, gk.neuerZielwert)}
                                    </span>
                                    <span className="text-stone-500 dark:text-slate-500"> / {eingabePeriodLabel}</span>
                                    {gk.notiz ? (
                                        <span className="mt-0.5 block text-sm text-stone-500 dark:text-slate-500">
                                            Notiz: {gk.notiz}
                                        </span>
                                    ) : null}
                                </p>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        removeGemeinkostenDetailAnpassung(gk.id);
                                        if (gkKey === gk.bwaKey) {
                                            setGkKey('');
                                            setGkZielwert('');
                                        }
                                    }}
                                    className="rounded p-1 text-stone-500 dark:text-slate-500 hover:bg-pe-hover dark:hover:bg-[#222] hover:text-rose-400"
                                    aria-label="Entfernen"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <div className="mt-6 border-b border-pe-border dark:border-[#222] pb-6">
                <SectionTitle>Personal-Struktur</SectionTitle>
                <p className="mb-3 text-sm leading-relaxed text-stone-500 dark:text-slate-500">
                    Wähle einen Mitarbeiter und passe den Zielwert an — Änderungen wirken sofort im Report.
                    Mit <span className="text-pe-accent-text dark:text-[#E6C299]">+</span> übernimmst du den Eintrag und wählst den
                    nächsten Mitarbeiter.
                </p>

                <div className="mb-3 space-y-2">
                    <select
                        value={paMitarbeiterId}
                        onChange={(e) => {
                            const id = e.target.value;
                            if (id === PE_PROFIT_NEUER_MITARBEITER_ID) {
                                setPaNeuDraftId(null);
                                setPaNeuName('');
                                setPaZielwert('');
                                setPaNeuUmsatzFaktor(PE_PROFIT_DEFAULT_UMSATZ_FAKTOR_NEU);
                            }
                            setPaMitarbeiterId(id);
                        }}
                        data-pe-tooltip="Mitarbeiter wählen oder „+ Neuer Mitarbeiter“. Danach Zielwert tippen."
                        className="w-full rounded-lg border border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-[#1a1a1a] px-3 py-2.5 text-base text-pe-text dark:text-white outline-none focus:border-[#E6C299]/50"
                    >
                        <option value="">Mitarbeiter wählen …</option>
                        <option value={PE_PROFIT_NEUER_MITARBEITER_ID}>+ Neuer Mitarbeiter</option>
                        {availableMaOptions.map((ma) => (
                            <option key={ma.id} value={ma.id}>
                                {ma.name} ({ma.gruppe})
                                {usedPaIds.has(ma.id) ? ' (bearbeiten)' : ''}
                            </option>
                        ))}
                    </select>

                    {selectedMaIstBetrag !== null && (
                        <p className="text-sm text-pe-accent-text dark:text-[#E6C299]/90">
                            Aktueller IST-Wert (Personalkosten): {formatCurrency(selectedMaIstBetrag)} /{' '}
                            {periodLabel}
                        </p>
                    )}

                    {paMitarbeiterId === PE_PROFIT_NEUER_MITARBEITER_ID && (
                        <>
                            <input
                                type="text"
                                value={paNeuName}
                                onChange={(e) => {
                                    const name = e.target.value;
                                    setPaNeuName(name);
                                    const draftId = paNeuDraftId;
                                    if (draftId) {
                                        useAppStore.getState().updatePersonalAnpassung(draftId, {
                                            mitarbeiterName: name.trim() || 'Neuer Mitarbeiter',
                                        });
                                    }
                                }}
                                placeholder="Bezeichnung (z. B. neue Stylistin)"
                                className="w-full rounded-lg border border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-[#1a1a1a] px-3 py-2.5 text-base text-pe-text dark:text-white outline-none focus:border-[#E6C299]/50"
                            />
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-pe-muted dark:text-slate-300">
                                    Umsatzfaktor (Neuer Mitarbeiter)
                                </label>
                                <NumberStepInput
                                    value={paNeuUmsatzFaktor}
                                    onChangeNumber={(next) => {
                                        const faktor = parseDecimalNumber(next);
                                        setPaNeuUmsatzFaktor(faktor);
                                        const draftId = paNeuDraftId;
                                        if (draftId) {
                                            useAppStore.getState().updatePersonalAnpassung(draftId, {
                                                umsatzFaktorNeu: faktor,
                                            });
                                        }
                                    }}
                                    step={0.1}
                                    min={0}
                                    max={10}
                                    precision="decimal"
                                    className="w-full"
                                    tooltip="Umsatzfaktor: geplantes Gehalt × Faktor = erwarteter Zusatzumsatz (Standard 2,5)."
                                />
                                <p className="text-sm text-stone-500 dark:text-slate-500">
                                    Zusatzumsatz = Geplantes Gehalt × Faktor (Standard: 2,5).
                                </p>
                            </div>
                        </>
                    )}

                    {paMitarbeiterId &&
                        paMitarbeiterId !== PE_PROFIT_NEUER_MITARBEITER_ID && (
                            <label
                                className="flex cursor-pointer items-center gap-2 rounded-lg border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] px-3 py-2.5 text-sm text-pe-muted dark:text-slate-300"
                                data-pe-tooltip="Personalkosten auf 0 setzen und optional den Umsatz-Auffang steuern."
                            >
                                <input
                                    type="checkbox"
                                    checked={paEntlassen}
                                    onChange={(e) =>
                                        commitPersonalEntlassenFelder({ entlassen: e.target.checked })
                                    }
                                    className="h-4 w-4 rounded border-pe-border-strong dark:border-[#444] bg-pe-elevated dark:bg-[#1a1a1a] text-pe-accent-text dark:text-[#E6C299]"
                                />
                                Mitarbeiter entlassen / freistellen
                            </label>
                        )}

                    {paEntlassen && (
                        <div className="space-y-3 rounded-lg border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] p-3">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-pe-muted dark:text-slate-300">
                                    Bisheriger Umsatzfaktor
                                </label>
                                <NumberStepInput
                                    value={paBisherigerUmsatzFaktor}
                                    onChangeNumber={(next) =>
                                        commitPersonalEntlassenFelder({
                                            bisherigerUmsatzFaktor: parseDecimalNumber(next),
                                        })
                                    }
                                    step={0.1}
                                    min={0}
                                    max={10}
                                    precision="decimal"
                                    className="w-full"
                                    tooltip="Bisheriger Umsatzfaktor des Mitarbeiters (für Auffang-Rechnung)."
                                />
                            </div>
                            <PercentSlider
                                label="Umsatz wird aufgefangen zu"
                                hint="Wie viel Prozent des bisherigen Umsatzes bleibt im Betrieb?"
                                value={paUmsatzAuffangProzent}
                                min={0}
                                max={100}
                                step={5}
                                onChange={(value) =>
                                    commitPersonalEntlassenFelder({ umsatzAuffangProzent: value })
                                }
                            />
                        </div>
                    )}

                    <div className="flex min-w-0 gap-2">
                        <NumberStepInput
                            value={paZielwert}
                            typingMode={paMitarbeiterId === PE_PROFIT_NEUER_MITARBEITER_ID}
                            onChange={setPaZielwert}
                            onChangeNumber={(next) => {
                                const isNeu = paMitarbeiterId === PE_PROFIT_NEUER_MITARBEITER_ID;
                                const snapped = isNeu
                                    ? next
                                    : resolveStepTarget(next, reportMoneyStep, selectedMaIstBetrag ?? undefined);
                                setPaZielwert(roundEuroString(snapped));
                                if (paMitarbeiterId) commitPersonalZielwert(snapped);
                            }}
                            onBlur={() => {
                                if (
                                    paMitarbeiterId === PE_PROFIT_NEUER_MITARBEITER_ID &&
                                    paZielwert !== '' &&
                                    paZielwert !== '-'
                                ) {
                                    commitPersonalZielwert(parseInputNumber(paZielwert));
                                }
                            }}
                            step={reportMoneyStep}
                            min={0}
                            snapTo={
                                paMitarbeiterId === PE_PROFIT_NEUER_MITARBEITER_ID
                                    ? undefined
                                    : selectedMaIstBetrag ?? undefined
                            }
                            placeholder={
                                paMitarbeiterId === PE_PROFIT_NEUER_MITARBEITER_ID
                                    ? `Geplante Gesamtkosten (${currencyCode} / ${periodLabel})`
                                    : paEntlassen
                                      ? `Personalkosten 0 (entlassen)`
                                      : `Zielwert Personalkosten (${currencyCode} / ${periodLabel})`
                            }
                            className={`min-w-0 flex-1 ${paMitarbeiterId && !paEntlassen ? '' : paMitarbeiterId && paEntlassen ? 'pointer-events-none opacity-50' : 'pointer-events-none opacity-40'}`}
                            tooltip="Personalkosten-Ziel in der Report-Ansicht (Monat/Jahr oben). Folgt dem Ansicht-Schalter."
                        />
                        <button
                            type="button"
                            onClick={handlePersonalUebernehmenUndWeiter}
                            disabled={!paMitarbeiterId}
                            data-pe-tooltip="Übernehmen und nächsten Mitarbeiter wählen"
                            title="Übernehmen und nächsten Mitarbeiter wählen"
                            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-[#E6C299]/40 bg-[#E6C299]/15 px-3 py-2.5 text-sm font-bold uppercase tracking-wider text-pe-accent-text dark:text-[#E6C299] hover:bg-[#E6C299]/25 disabled:opacity-40"
                        >
                            <Plus size={14} />
                        </button>
                    </div>
                    {paMitarbeiterId && paEingabeAktuell !== null && !paEntlassen && (
                        <IstDeltaAnzeige delta={paDelta} periodLabel={periodLabel} currencyCode={currencyCode} />
                    )}
                    {paEntlassen && selectedMaIstBetrag !== null && (
                        <IstDeltaAnzeige
                            delta={roundEuro(0 - selectedMaIstBetrag)}
                            periodLabel={periodLabel}
                            currencyCode={currencyCode}
                        />
                    )}

                    {paHatAbweichung && selectedMaIstBetrag !== null && (
                        <button
                            type="button"
                            onClick={resetPersonalToIst}
                            className="text-sm font-medium text-pe-accent-text dark:text-[#E6C299] underline-offset-2 hover:underline"
                        >
                            Zurück auf IST-Wert ({formatCurrency(selectedMaIstBetrag)} / {periodLabel})
                        </button>
                    )}
                </div>

                {(personalAnpassungen ?? []).length === 0 ? (
                    <p className="text-sm text-slate-600">Keine Personal-Anpassungen hinterlegt.</p>
                ) : (
                    <ul key={`pa-list-${peProfitDataVersion}`} className="space-y-2">
                        {(personalAnpassungen ?? []).map((pa) => (
                            <li
                                key={pa.id}
                                className="flex cursor-pointer items-center gap-2 rounded-lg border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] px-4 py-3 hover:border-[#E6C299]/30"
                                onClick={() => {
                                    if (pa.mitarbeiterId === PE_PROFIT_NEUER_MITARBEITER_ID) {
                                        setPaMitarbeiterId(PE_PROFIT_NEUER_MITARBEITER_ID);
                                        setPaNeuName(pa.mitarbeiterName);
                                        setPaNeuDraftId(pa.id);
                                        setPaNeuUmsatzFaktor(
                                            pa.umsatzFaktorNeu ?? PE_PROFIT_DEFAULT_UMSATZ_FAKTOR_NEU
                                        );
                                        setPaZielwert(
                                            formatEingabeFromMonat(pa.neuerZielwert, ansichtsModus, zeitraum)
                                        );
                                    } else {
                                        setPaMitarbeiterId(pa.mitarbeiterId);
                                    }
                                }}
                            >
                                <p className="min-w-0 flex-1 text-base text-pe-text dark:text-slate-200">
                                    <span className="text-pe-text dark:text-white">{pa.mitarbeiterName || 'Mitarbeiter'}:</span>{' '}
                                    <span className="text-pe-accent-text dark:text-[#E6C299]">
                                        {pa.entlassen
                                            ? `${formatAnpassungSummary(pa.istWert, 0, ansichtsModus)} (entlassen)`
                                            : formatAnpassungSummary(pa.istWert, pa.neuerZielwert, ansichtsModus)}
                                    </span>
                                    <span className="text-stone-500 dark:text-slate-500"> / {periodLabel}</span>
                                </p>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        removePersonalAnpassung(pa.id);
                                        if (
                                            paMitarbeiterId === pa.mitarbeiterId ||
                                            paNeuDraftId === pa.id
                                        ) {
                                            setPaMitarbeiterId('');
                                            setPaZielwert('');
                                            setPaNeuName('');
                                            setPaNeuDraftId(null);
                                        }
                                    }}
                                    className="rounded p-1 text-stone-500 dark:text-slate-500 hover:bg-pe-hover dark:hover:bg-[#222] hover:text-rose-400"
                                    aria-label="Entfernen"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <div className="mt-6 border-b border-pe-border dark:border-[#222] pb-6">
                <SectionTitle>Geplante Investitionen</SectionTitle>
                <p className="mb-3 text-sm leading-relaxed text-stone-500 dark:text-slate-500">{invHint}</p>

                <div className="mb-3 space-y-2">
                    <input
                        type="text"
                        value={invName}
                        onChange={(e) => setInvName(e.target.value)}
                        placeholder="Bezeichnung (z. B. neue Waschplätze)"
                        className="w-full rounded-lg border border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-[#1a1a1a] px-3 py-2.5 text-base text-pe-text dark:text-white outline-none focus:border-[#E6C299]/50"
                    />
                    <BetragAlsLeiste value={kostenEingabeModus} onChange={applyKostenEingabeModus} />
                    <NumberStepInput
                        value={invMonatskosten}
                        onChange={setInvMonatskosten}
                        step={moneyStep}
                        placeholder={invBetragPlaceholder}
                        className="w-full"
                        tooltip="Investitionsbetrag tippen. „Jahr“ = Jahreskosten (z. B. Leasing p. a.), wird intern auf Monat umgerechnet."
                    />
                    <EingabeUmrechnungHint
                        eingabe={
                            invMonatskosten === '' || invMonatskosten === '-'
                                ? null
                                : parseInputNumber(invMonatskosten)
                        }
                        modus={kostenEingabeModus}
                        zeitraum={zeitraum}
                        formatCurrency={formatCurrency}
                    />
                    <button
                        type="button"
                        onClick={handleAddInvestition}
                        data-pe-tooltip="Investition zur Prognose übernehmen"
                        className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-[#E6C299]/40 bg-[#E6C299]/15 px-3 py-2.5 text-sm font-bold uppercase tracking-wider text-pe-accent-text dark:text-[#E6C299] hover:bg-[#E6C299]/25"
                    >
                        <Plus size={14} />
                        Hinzufügen
                    </button>
                </div>

                {(neueInvestitionen ?? []).length === 0 ? (
                    <p className="text-sm text-slate-600">Keine Investitionen hinterlegt.</p>
                ) : (
                    <ul key={`inv-list-${peProfitDataVersion}`} className="space-y-2">
                        {(neueInvestitionen ?? []).map((inv) => (
                            <li
                                key={inv.id}
                                className="flex items-center gap-2 rounded-lg border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] px-4 py-3"
                            >
                                <p className="min-w-0 flex-1 text-base text-pe-text dark:text-slate-200">
                                    <span className="text-pe-text dark:text-white">{inv.name || 'Investition'}:</span>{' '}
                                    <span className="text-pe-accent-text dark:text-[#E6C299]">
                                        {formatCurrency(
                                            Number(
                                                toEingabeFromMonat(
                                                    inv.monatskosten,
                                                    kostenEingabeModus,
                                                    zeitraum
                                                )
                                            )
                                        )}
                                    </span>
                                    <span className="text-stone-500 dark:text-slate-500"> / {eingabePeriodLabel}</span>
                                </p>
                                <button
                                    type="button"
                                    onClick={() => removeNeueInvestition(inv.id)}
                                    className="rounded p-1 text-stone-500 dark:text-slate-500 hover:bg-pe-hover dark:hover:bg-[#222] hover:text-rose-400"
                                    aria-label="Entfernen"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <div className="mt-6">
                <SectionTitle>Neue Dienstleistungen</SectionTitle>
                <p className="mb-3 text-sm leading-relaxed text-stone-500 dark:text-slate-500">{dlUmsatzHint}</p>

                <div className="mb-3 space-y-2">
                    <input
                        type="text"
                        value={dlName}
                        onChange={(e) => setDlName(e.target.value)}
                        placeholder="Bezeichnung (z. B. Keratin-Behandlung)"
                        className="w-full rounded-lg border border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-[#1a1a1a] px-3 py-2.5 text-base text-pe-text dark:text-white outline-none focus:border-[#E6C299]/50"
                    />
                    <NumberStepInput
                        value={dlUmsatz}
                        onChange={setDlUmsatz}
                        step={reportMoneyStep}
                        placeholder={dlUmsatzPlaceholder}
                        className="w-full"
                        tooltip="Zusatzumsatz der neuen Dienstleistung in der Report-Ansicht (Monat/Jahr oben)."
                    />
                    <button
                        type="button"
                        onClick={handleAddDienstleistung}
                        data-pe-tooltip="Dienstleistung zur Prognose hinzufügen"
                        className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-[#E6C299]/40 bg-[#E6C299]/15 px-3 py-2.5 text-sm font-bold uppercase tracking-wider text-pe-accent-text dark:text-[#E6C299] hover:bg-[#E6C299]/25"
                    >
                        <Plus size={14} />
                        Hinzufügen
                    </button>
                </div>

                {(neueDienstleistungen ?? []).length === 0 ? (
                    <p className="text-sm text-slate-600">Noch keine Erweiterungen hinterlegt.</p>
                ) : (
                    <ul key={`dl-list-${peProfitDataVersion}`} className="space-y-2">
                        {(neueDienstleistungen ?? []).map((dl) => (
                            <li
                                key={dl.id}
                                className="flex items-center gap-2 rounded-lg border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] px-4 py-3"
                            >
                                <input
                                    type="checkbox"
                                    checked={dl.aktiv}
                                    onChange={(e) =>
                                        updateNeueDienstleistung(dl.id, { aktiv: e.target.checked })
                                    }
                                    className="h-5 w-5 rounded border-pe-border-strong dark:border-[#444] bg-pe-elevated dark:bg-[#1a1a1a] text-pe-accent-text dark:text-[#E6C299]"
                                />
                                <div className="min-w-0 flex-1">
                                    <input
                                        type="text"
                                        value={dl.name}
                                        onChange={(e) =>
                                            updateNeueDienstleistung(dl.id, { name: e.target.value })
                                        }
                                        className="w-full bg-transparent text-base text-pe-text dark:text-white outline-none"
                                    />
                                    <NumberStepInput
                                        value={toEingabeFromJahreswert(
                                            dl.jahresumsatzNetto,
                                            ansichtsModus,
                                            zeitraum
                                        )}
                                        onChangeNumber={(next) =>
                                            updateNeueDienstleistung(dl.id, {
                                                jahresumsatzNetto: toJahreswertFromEingabe(
                                                    next,
                                                    ansichtsModus,
                                                    zeitraum
                                                ),
                                            })
                                        }
                                        step={reportMoneyStep}
                                        size="compact"
                                        className="mt-1"
                                        inputClassName="text-sm text-pe-accent-text dark:text-[#E6C299]"
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={() => removeNeueDienstleistung(dl.id)}
                                    className="rounded p-1 text-stone-500 dark:text-slate-500 hover:bg-pe-hover dark:hover:bg-[#222] hover:text-rose-400"
                                    aria-label="Entfernen"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            </div>
        </aside>
    );
};

export default PEProfitControlPanel;
